/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import exportPriceList from "@stoneage/App/Handlers/PriceList/PriceList";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { color } from "@stoneage/XDL/Utilities";
import React from "react";
import { connect, ConnectedProps } from "react-redux";

import { css } from "styled-components";

const enum fields {
    priceListText = "priceListText",
    buttonText = "buttonText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.priceListText]: string;
        [fields.buttonText]: string;
    };
    extendedStyles?: PriceListStyles;
}

type Props = OwnProps & PropFromRedux;

export interface PriceListStyles {
    container: InjectableCss;
    message: TypographyProps;
}

export const priceListStyles: PriceListStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            background-color: ${color("common.ui4")};
            width: 100%;
            padding: 12px 20px;
        `,
    },
    message: {
        css: css`
            margin-bottom: 0;
            align-self: center;
        `,
    },
};

const PriceList = ({
    id,
    fields: { priceListText, buttonText },
    isAuthenticated,
    exportPriceList,
    extendedStyles,
}: Props) => {
    if (!isAuthenticated) {
        return <></>;
    }

    const clickHandler = () => {
        exportPriceList({ buttonLabel: buttonText });
    };

    const [styles] = React.useState(() => mergeToNew(priceListStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.container}>
            <Typography variant="h4" {...styles.message}>
                {priceListText}
            </Typography>
            <Button onClick={clickHandler}>{buttonText}</Button>
        </StyledWrapper>
    );
};

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
    };
};

const mapDispatchToProps = {
    exportPriceList,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropFromRedux = ConnectedProps<typeof connector>;

const priceList: WidgetModule = {
    component: connector(PriceList),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.priceListText,
                displayName: "Price List Text",
                editorTemplate: "TextField",
                defaultValue: translate(`The ${new Date().getFullYear()} Price List .PDF Is Available`),
                fieldType: "Translatable",
            },
            {
                name: fields.buttonText,
                displayName: "Download Button Text",
                editorTemplate: "TextField",
                defaultValue: "Download Price List",
                fieldType: "Translatable",
            },
        ],
    },
};

export default priceList;
